import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Select, InputNumber, Space } from 'antd';
import PropTypes from 'prop-types';
import { Input } from '../Input';
import { Button } from '../Button';
import useWorkspaces from '../../hooks/useWorkspaces';
import Loader from '../Loader';
import httpClient from '../../datasources/httpClient';
import {
  Form, SubmitArea,
} from './styles';
import { dayToWeekNumber } from '../../utils/format';

const { Option } = Select;

export default function BackupForm({ backupId }) {
  const [name, setName] = useState('');
  const [companyId, setCompanyId] = useState(undefined);
  const [workspaceId, setWorkspaceId] = useState(undefined);
  const [dropletId, setDropletId] = useState(undefined);
  const [droplets, setDroplets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [failure, setFailure] = useState(null);
  const [interval, setInterval] = useState('h');
  const [days, setDays] = useState([]);
  const [intervalValue, setIntervalValue] = useState(1);
  const [callbackUrl, setCallbackUrl] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [backup, setBackup] = useState(null);

  const { workspaces } = useWorkspaces();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(
    () => {
      setIsLoading(true);

      const fetchDroplets = async () => {
        const response = await httpClient.get('droplets');

        if (response.status === 200 && response.data.length) {
          setDroplets(response.data);
        }
      };

      const fetchCompanies = async () => {
        const response = await httpClient.get('companies');

        if (response.status === 200 && response.data.length) {
          setCompanies(response.data);
        }
      };

      const handleFetchBackup = async (id) => {
        const response = await httpClient.get(`backups/${id}`);

        if (response.status === 200) {
          setBackup(response.data);
        }
      };

      if (backupId) {
        Promise.all([
          fetchDroplets(),
          fetchCompanies(),
          handleFetchBackup(backupId),
        ]).finally(() => setIsLoading(false));
      } else {
        Promise.all([
          fetchDroplets(),
          fetchCompanies(),
        ]).finally(() => setIsLoading(false));
      }
    },
    [backupId],
  );

  useEffect(() => {
    if (backup) {
      setName(backup.name);
      setDropletId(backup.dropletId ?? undefined);
      setCompanyId(backup.companyId);
      setWorkspaceId(backup.workspaceId);
      setTargetUrl(backup.targetUrl);
      setCallbackUrl(backup.callbackUrl);
      setIntervalValue(backup.schedule.intervalValue);
      setInterval(backup.schedule.interval.charAt(0).toLocaleLowerCase());
      setDays(backup.schedule.days.map((day) => dayToWeekNumber(day)));
    }
  }, [backup]);

  async function handleCreateBackup(event) {
    try {
      event.preventDefault();
      setIsLoading(true);

      const response = await httpClient.post(
        'backups',
        {
          name,
          workspaceId,
          dropletId,
          companyId,
          days,
          interval,
          intervalValue,
          callbackUrl,
          targetUrl,
        },
      );
      if (response.status === 201) {
        queryClient.invalidateQueries('backups');

        navigator.clipboard.writeText(`${process.env.REACT_APP_API_ADDRESS}/reports/${response.data.id}`);
        navigate('/home');
      }
    } catch (error) {
      if (error.response.data) {
        const { message } = error.response.data;

        setFailure(message);
      } else {
        setFailure(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleEditBackup(event) {
    try {
      event.preventDefault();
      setIsLoading(true);

      const response = await httpClient.patch(
        `backups/${backupId}`,
        {
          name,
          workspaceId,
          dropletId,
          companyId,
          days,
          interval,
          intervalValue,
          callbackUrl,
          targetUrl,
        },
      );
      if (response.status === 202) {
        queryClient.invalidateQueries('backups');

        navigator.clipboard.writeText(`${process.env.REACT_APP_API_ADDRESS}reports/${response.data.id}`);
        navigate('/home');
      }
    } catch (error) {
      if (error.response.data) {
        const { message } = error.response.data;

        setFailure(message);
      } else {
        setFailure(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (<Loader />);
  }

  return (
    <Form onSubmit={(event) => (backupId ? handleEditBackup(event) : handleCreateBackup(event))}>
      <Input placeholder="Nome" value={name} onChange={(event) => setName(event.target.value)} />
      <Select
        showSearch
        optionFilterProp="children"
        placeholder="Escolha um Cliente"
        required
        value={companyId}
        onSelect={(value) => setCompanyId(value)}
        filterOption={(input, option) => String(option.children).includes(input)}
      >
        {companies?.map((company) => (
          <Option
            key={company.id}
            value={company.id}
          >
            {company.name}
          </Option>
        ))}
      </Select>
      <Select
        showSearch
        optionFilterProp="children"
        placeholder="Escolha um Workspace"
        required
        value={workspaceId}
        onSelect={(value) => setWorkspaceId(value)}
        filterOption={(input, option) => String(option.children).includes(input)}
      >
        {workspaces?.map((workspace) => (
          <Option
            key={workspace.id}
            value={workspace.id}
          >
            {workspace.name}
          </Option>
        ))}
      </Select>
      <Select
        showSearch
        optionFilterProp="children"
        placeholder="Escolha um droplet"
        required
        value={dropletId}
        onSelect={(value) => setDropletId(value)}
        filterOption={(input, option) => String(option.children).includes(input)}
      >
        {
          droplets?.map((droplet) => (
            <Option
              key={droplet.id}
              value={droplet.id}
            >
              {droplet.name}
            </Option>
          ))
        }
      </Select>
      <Select
        mode="multiple"
        allowClear
        placeholder="Escolha os dias"
        value={days}
        onChange={(value) => setDays(value)}
      >
        <Option value={0}>Domingo</Option>
        <Option value={1}>Segunda</Option>
        <Option value={2}>Terça</Option>
        <Option value={3}>Quarta</Option>
        <Option value={4}>Quinta</Option>
        <Option value={5}>Sexta</Option>
        <Option value={6}>Sábado</Option>
      </Select>
      <Space style={{
        marginTop: '16px',
      }}
      >
        <InputNumber
          min={1}
          max={24}
          defaultValue={intervalValue}
          value={intervalValue}
          onChange={(value) => setIntervalValue(value)}
        />
        <Select
          placeholder="Escolha o intervalo"
          defaultValue={interval}
          value={interval}
          onSelect={(value) => setInterval(value)}
        >
          <Option value="m">Minutos</Option>
          <Option value="h">Horas</Option>
          <Option value="d">Dias</Option>
          <Option value="w">Semanas</Option>
        </Select>
      </Space>
      <Input placeholder="URL da callback" value={callbackUrl} onChange={(event) => setCallbackUrl(event.target.value)} />
      <Input placeholder="URL do backup" value={targetUrl} onChange={(event) => setTargetUrl(event.target.value)} />
      <SubmitArea>
        {failure && <span>{failure}</span>}
        {isLoading && workspaceId && dropletId
          ? <Loader />
          : <Button type="submit">{backupId ? 'Editar Backup' : 'Registrar Backup'}</Button>}
      </SubmitArea>
    </Form>
  );
}

BackupForm.propTypes = {
  backupId: PropTypes.string,
};

BackupForm.defaultProps = {
  backupId: undefined,
};
